import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Save, Image as ImageIcon } from 'lucide-react';
import { getSiteSettings, updateSiteSettings } from '../services/api';
import { toast } from 'sonner';
import { MediaLibrary } from './MediaLibrary';

export const AboutPageManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSiteSettings();
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await updateSiteSettings(settings);
      toast.success('About page settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
      console.error(error);
    }
  };

  const handleSelectImage = (field) => {
    setCurrentImageField(field);
    setShowMediaLibrary(true);
  };

  const handleMediaSelect = (selectedUrls) => {
    if (currentImageField && selectedUrls.length > 0) {
      setSettings({ ...settings, [currentImageField]: selectedUrls[0] });
    }
    setShowMediaLibrary(false);
    setCurrentImageField(null);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-600 dark:text-gray-400">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSave} className="space-y-6">
        {/* Site Images Section */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">Site Images</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Home Hero Image */}
            <div>
              <Label>Home Page Hero Image</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={settings?.home_hero_image || ''}
                  onChange={(e) => setSettings({ ...settings, home_hero_image: e.target.value })}
                  placeholder="Image URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSelectImage('home_hero_image')}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select
                </Button>
              </div>
              <div className="mt-2">
                <Label className="text-sm">Image Fit</Label>
                <Select
                  value={settings?.home_hero_image_fit || 'cover'}
                  onValueChange={(value) => setSettings({ ...settings, home_hero_image_fit: value })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select image fit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover (Fill area, crop if needed)</SelectItem>
                    <SelectItem value="contain">Contain (Fit within area)</SelectItem>
                    <SelectItem value="fill">Fill (Stretch to fill)</SelectItem>
                    <SelectItem value="scale-down">Scale Down (Shrink if needed)</SelectItem>
                    <SelectItem value="none">None (Original size)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {settings?.home_hero_image && (
                <img 
                  src={settings.home_hero_image}
                  alt="Home hero" 
                  className="mt-2 h-32 w-full object-cover rounded"
                />
              )}
            </div>

            {/* About Hero Image */}
            <div>
              <Label>About Page Hero Image</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={settings?.about_hero_image || ''}
                  onChange={(e) => setSettings({ ...settings, about_hero_image: e.target.value })}
                  placeholder="Image URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSelectImage('about_hero_image')}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select
                </Button>
              </div>
              <div className="mt-2">
                <Label className="text-sm">Image Fit</Label>
                <Select
                  value={settings?.about_hero_image_fit || 'cover'}
                  onValueChange={(value) => setSettings({ ...settings, about_hero_image_fit: value })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select image fit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover (Fill area, crop if needed)</SelectItem>
                    <SelectItem value="contain">Contain (Fit within area)</SelectItem>
                    <SelectItem value="fill">Fill (Stretch to fill)</SelectItem>
                    <SelectItem value="scale-down">Scale Down (Shrink if needed)</SelectItem>
                    <SelectItem value="none">None (Original size)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {settings?.about_hero_image && (
                <img 
                  src={settings.about_hero_image}
                  alt="About hero" 
                  className="mt-2 h-32 w-full object-cover rounded"
                />
              )}
            </div>

            {/* About Story Image */}
            <div>
              <Label>About Page Story Image</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={settings?.about_story_image || ''}
                  onChange={(e) => setSettings({ ...settings, about_story_image: e.target.value })}
                  placeholder="Image URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSelectImage('about_story_image')}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select
                </Button>
              </div>
              <div className="mt-2">
                <Label className="text-sm">Image Fit</Label>
                <Select
                  value={settings?.about_story_image_fit || 'cover'}
                  onValueChange={(value) => setSettings({ ...settings, about_story_image_fit: value })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select image fit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover (Fill area, crop if needed)</SelectItem>
                    <SelectItem value="contain">Contain (Fit within area)</SelectItem>
                    <SelectItem value="fill">Fill (Stretch to fill)</SelectItem>
                    <SelectItem value="scale-down">Scale Down (Shrink if needed)</SelectItem>
                    <SelectItem value="none">None (Original size)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {settings?.about_story_image && (
                <img 
                  src={settings.about_story_image}
                  alt="About story" 
                  className="mt-2 h-32 w-full object-cover rounded"
                />
              )}
            </div>

            {/* About Team Image */}
            <div>
              <Label>Location Banner Background Image</Label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Used for the "Serving Lynchburg with Pride" section</p>
              <div className="flex gap-2 mt-2">
                <Input
                  value={settings?.about_team_image || ''}
                  onChange={(e) => setSettings({ ...settings, about_team_image: e.target.value })}
                  placeholder="Image URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSelectImage('about_team_image')}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select
                </Button>
              </div>
              <div className="mt-2">
                <Label className="text-sm">Image Fit</Label>
                <Select
                  value={settings?.about_team_image_fit || 'cover'}
                  onValueChange={(value) => setSettings({ ...settings, about_team_image_fit: value })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select image fit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover (Fill area, crop if needed)</SelectItem>
                    <SelectItem value="contain">Contain (Fit within area)</SelectItem>
                    <SelectItem value="fill">Fill (Stretch to fill)</SelectItem>
                    <SelectItem value="scale-down">Scale Down (Shrink if needed)</SelectItem>
                    <SelectItem value="none">None (Original size)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {settings?.about_team_image && (
                <img 
                  src={settings.about_team_image}
                  alt="About team" 
                  className="mt-2 h-32 w-full object-cover rounded"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* About Page Content Section */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">About Page Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Hero Section */}
            <div>
              <Label htmlFor="about_hero_title">Hero Title</Label>
              <Input
                id="about_hero_title"
                value={settings?.about_hero_title || ''}
                onChange={(e) => setSettings({ ...settings, about_hero_title: e.target.value })}
                placeholder="About Otter River Properties"
              />
            </div>

            <div>
              <Label htmlFor="about_hero_subtitle">Hero Subtitle</Label>
              <Input
                id="about_hero_subtitle"
                value={settings?.about_hero_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, about_hero_subtitle: e.target.value })}
                placeholder="Building communities and creating homes since 2008"
              />
            </div>

            {/* Values Section */}
            <div>
              <Label htmlFor="about_values_title">Values Section Title</Label>
              <Input
                id="about_values_title"
                value={settings?.about_values_title || ''}
                onChange={(e) => setSettings({ ...settings, about_values_title: e.target.value })}
                placeholder="Our Core Values"
              />
            </div>

            <div>
              <Label htmlFor="about_values_subtitle">Values Section Subtitle</Label>
              <Textarea
                id="about_values_subtitle"
                value={settings?.about_values_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, about_values_subtitle: e.target.value })}
                placeholder="The principles that guide everything we do"
                rows={2}
              />
            </div>

            {/* Team Section */}
            <div>
              <Label htmlFor="about_team_title">Team Section Title</Label>
              <Input
                id="about_team_title"
                value={settings?.about_team_title || ''}
                onChange={(e) => setSettings({ ...settings, about_team_title: e.target.value })}
                placeholder="Meet Our Team"
              />
            </div>

            <div>
              <Label htmlFor="about_team_subtitle">Team Section Subtitle</Label>
              <Textarea
                id="about_team_subtitle"
                value={settings?.about_team_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, about_team_subtitle: e.target.value })}
                placeholder="Experienced professionals dedicated to your success"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="about_team_description">Team Description</Label>
              <Textarea
                id="about_team_description"
                value={settings?.about_team_description || ''}
                onChange={(e) => setSettings({ ...settings, about_team_description: e.target.value })}
                placeholder="Our local team knows the Lynchburg area inside and out..."
                rows={3}
              />
            </div>

            {/* Location Banner Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 dark:text-white">Location Banner Section</h4>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="about_location_title">Location Banner Title</Label>
                  <Input
                    id="about_location_title"
                    value={settings?.about_location_title || ''}
                    onChange={(e) => setSettings({ ...settings, about_location_title: e.target.value })}
                    placeholder="Serving Lynchburg with Pride"
                  />
                </div>

                <div>
                  <Label htmlFor="about_location_description">Location Banner Description</Label>
                  <Textarea
                    id="about_location_description"
                    value={settings?.about_location_description || ''}
                    onChange={(e) => setSettings({ ...settings, about_location_description: e.target.value })}
                    placeholder="Our local team knows the Lynchburg area inside and out..."
                    rows={2}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Note: The background image for this section uses the "About Page Team Image" from above.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Section */}
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardHeader>
            <CardTitle className="dark:text-white">About Page Statistics</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="stat_years_service">Years of Service</Label>
              <Input
                id="stat_years_service"
                value={settings?.stat_years_service || ''}
                onChange={(e) => setSettings({ ...settings, stat_years_service: e.target.value })}
                placeholder="15+"
              />
            </div>

            <div>
              <Label htmlFor="stat_properties_managed">Properties Managed</Label>
              <Input
                id="stat_properties_managed"
                value={settings?.stat_properties_managed || ''}
                onChange={(e) => setSettings({ ...settings, stat_properties_managed: e.target.value })}
                placeholder="500+"
              />
            </div>

            <div>
              <Label htmlFor="stat_happy_tenants">Happy Tenants</Label>
              <Input
                id="stat_happy_tenants"
                value={settings?.stat_happy_tenants || ''}
                onChange={(e) => setSettings({ ...settings, stat_happy_tenants: e.target.value })}
                placeholder="1000+"
              />
            </div>

            <div>
              <Label htmlFor="stat_satisfaction_rate">Satisfaction Rate</Label>
              <Input
                id="stat_satisfaction_rate"
                value={settings?.stat_satisfaction_rate || ''}
                onChange={(e) => setSettings({ ...settings, stat_satisfaction_rate: e.target.value })}
                placeholder="98%"
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" className="bg-otter-green hover:bg-otter-green-600 text-white">
          <Save className="h-4 w-4 mr-2" />
          Save All Changes
        </Button>
      </form>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => {
          setShowMediaLibrary(false);
          setCurrentImageField(null);
        }}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};
